// P:\0PJ\synforge\frontend\app\settings\display\page.tsx v5 2026.07.27 23:22 - uses shared styles/personalDisplay.ts (server-backed), no more local duplicate logic
"use client"

import { useEffect, useState } from "react"
import { Sun, Moon, Plus, X } from "lucide-react"
import {
  PersonalDisplaySettings as Settings,
  DEFAULT_DISPLAY_SETTINGS as DEFAULT_SETTINGS,
  BUILTIN_FONTS,
  loadPersonalDisplaySettings,
  applyPersonalDisplaySettings,
  savePersonalDisplaySettings,
} from "@/styles/personalDisplay"

const MEETING_SIZE_OPTIONS = [
  { id: "small",   label: "Small",   desc: "Smaller than base" },
  { id: "default", label: "Default", desc: "Same as base"      },
  { id: "large",   label: "Large",   desc: "Larger than base"  },
]

// ============================================================
// 메인 컴포넌트
// ============================================================
export default function DisplaySettingsPage() {
  const [settings,   setSettings]  = useState<Settings>(DEFAULT_SETTINGS)
  const [loading,    setLoading]   = useState(true)
  const [newFont,    setNewFont]    = useState("")
  const [addingFont, setAddingFont] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadPersonalDisplaySettings().then((s) => {
      if (cancelled) return
      setSettings(s)
      applyPersonalDisplaySettings(s)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  // 변경 즉시 화면 적용 + (디바운스로) 서버 저장.
  const update = (patch: Partial<Settings>) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    applyPersonalDisplaySettings(next)
    savePersonalDisplaySettings(next)
  }

  const addCustomFont = () => {
    const name = newFont.trim()
    if (!name || settings.customFonts.includes(name)) return
    update({ customFonts: [...settings.customFonts, name] })
    setNewFont("")
    setAddingFont(false)
  }

  const removeCustomFont = (name: string) => {
    update({
      customFonts: settings.customFonts.filter(f => f !== name),
      fontFamily:  settings.fontFamily === name ? "system" : settings.fontFamily,
    })
  }

  const allFonts = [
    ...BUILTIN_FONTS,
    ...settings.customFonts.map(f => ({ id: f, label: f, css: `'${f}', sans-serif`, custom: true as const })),
  ]

  const meetingFontSize = Math.max(
    settings.meetingSize === "small" ? settings.fontSize - 2 :
    settings.meetingSize === "large" ? settings.fontSize + 2 : settings.fontSize,
    8
  )

  if (loading) {
    return <div className="p-6 text-white/30 text-sm">Loading...</div>
  }

  return (
    <div className="p-6 max-w-[720px]">

      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-xl font-semibold text-white mb-1">Display Settings</h1>
        <p className="text-white/40 text-sm">Changes apply instantly and are saved automatically</p>
      </div>

      <div className="space-y-5">

        {/* ---- Theme ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-white mb-1">Theme</h3>
          <p className="text-xs text-white/30 mb-4">Choose your preferred color scheme</p>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-white/50">
              <Moon size={14} className="text-purple-400" />
              Dark
            </div>

            <button
              onClick={() => update({ theme: settings.theme === "dark" ? "light" : "dark" })}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                settings.theme === "light" ? "bg-amber-400" : "bg-purple-600"
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                settings.theme === "light" ? "translate-x-7" : "translate-x-1"
              }`} />
            </button>

            <div className="flex items-center gap-2 text-xs text-white/50">
              <Sun size={14} className="text-amber-400" />
              Light
            </div>

            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-white/[0.05] text-white/30 capitalize">
              {settings.theme}
            </span>
          </div>
        </div>

        {/* ---- Font Size ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-white">Font Size</h3>
            <span className="text-lg font-bold text-purple-300">
              {settings.fontSize}<span className="text-xs text-white/30 ml-0.5">px</span>
            </span>
          </div>
          <p className="text-xs text-white/30 mb-5">Base font size for the entire app</p>

          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] text-white/30 w-4 text-right">6</span>
            <input
              type="range" min={6} max={16} step={1}
              value={settings.fontSize}
              onChange={(e) => update({ fontSize: Number(e.target.value) })}
              className="flex-1 h-1.5 appearance-none bg-white/10 rounded-full cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5
                [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-purple-500
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:border-2
                [&::-webkit-slider-thumb]:border-white/20"
            />
            <span className="text-[10px] text-white/30 w-4">16</span>
          </div>

          {/* 눈금 클릭 */}
          <div className="flex justify-between px-6 mb-4">
            {Array.from({ length: 11 }, (_, i) => i + 6).map((n) => (
              <button
                key={n}
                onClick={() => update({ fontSize: n })}
                className={`text-[9px] w-4 text-center transition-colors ${
                  settings.fontSize === n ? "text-purple-400 font-bold" : "text-white/20 hover:text-white/40"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          {/* 미리보기 */}
          <div className="bg-white/[0.03] rounded-xl px-4 py-3 border border-white/[0.05]">
            <p className="text-[10px] text-white/20 mb-1.5 uppercase tracking-wider">Preview</p>
            <p style={{ fontSize: settings.fontSize }} className="text-white/70 leading-relaxed">
              The quick brown fox jumps over the lazy dog. 빠른 갈색 여우가 게으른 개를 뛰어넘는다.
            </p>
          </div>
        </div>

        {/* ---- Font Family ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-white mb-1">Font Family</h3>
          <p className="text-xs text-white/30 mb-4">Select or add a custom font</p>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {allFonts.map((font) => (
              <div
                key={font.id}
                onClick={() => update({ fontFamily: font.id })}
                className={`relative flex items-center gap-3 px-3 py-3 rounded-xl border cursor-pointer transition-all ${
                  settings.fontFamily === font.id
                    ? "border-purple-500/50 bg-purple-500/10"
                    : "border-white/[0.06] hover:border-white/20 hover:bg-white/[0.03]"
                }`}
              >
                <div className={`w-3 h-3 rounded-full border-2 shrink-0 transition-colors ${
                  settings.fontFamily === font.id ? "border-purple-500 bg-purple-500" : "border-white/20"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white truncate">{font.label}</div>
                  <div className="text-[11px] text-white/40 mt-0.5" style={{ fontFamily: font.css }}>
                    Aa Bb Cc 123
                  </div>
                </div>
                {"custom" in font && Boolean(font.custom) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeCustomFont(font.id) }}
                    className="text-white/20 hover:text-red-400 transition-colors shrink-0"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {addingFont ? (
            <div className="flex gap-2">
              <input
                autoFocus
                value={newFont}
                onChange={(e) => setNewFont(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustomFont()}
                placeholder="Font name (e.g. Pretendard)"
                className="flex-1 bg-white/[0.04] border border-white/[0.07] rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/50"
              />
              <button onClick={addCustomFont}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-xs text-white transition-colors">
                Add
              </button>
              <button onClick={() => { setAddingFont(false); setNewFont("") }}
                className="px-3 py-2 bg-white/[0.04] border border-white/[0.07] rounded-lg text-xs text-white/40 transition-colors">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAddingFont(true)}
              className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              <Plus size={13} />
              Add custom font
            </button>
          )}
        </div>

        {/* ---- Border Radius ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-white">Border Radius</h3>
            <span className="text-lg font-bold text-purple-300">
              {settings.radius}<span className="text-xs text-white/30 ml-0.5">px</span>
            </span>
          </div>
          <p className="text-xs text-white/30 mb-5">Corner roundness for cards and panels</p>

          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] text-white/30 w-4 text-right">0</span>
            <input
              type="range" min={0} max={24} step={2}
              value={settings.radius}
              onChange={(e) => update({ radius: Number(e.target.value) })}
              className="flex-1 h-1.5 appearance-none bg-white/10 rounded-full cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5
                [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-purple-500
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-webkit-slider-thumb]:shadow-lg"
            />
            <span className="text-[10px] text-white/30 w-4">24</span>
          </div>

          {/* 프리셋 클릭 */}
          <div className="flex gap-3">
            {[0, 4, 8, 12, 16, 20, 24].map((r) => (
              <button
                key={r}
                onClick={() => update({ radius: r })}
                className={`flex-1 h-10 border transition-all text-[10px] ${
                  settings.radius === r
                    ? "border-purple-500/50 bg-purple-500/10 text-purple-300"
                    : "border-white/10 bg-white/[0.03] text-white/30 hover:border-white/20"
                }`}
                style={{ borderRadius: r }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Auto Logout ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-white mb-1">Auto Logout</h3>
          <p className="text-xs text-white/30 mb-4">
            Automatically log out after a period of inactivity
          </p>

          <div className="grid grid-cols-5 gap-2">
            {[0, 15, 30, 60, 120].map((m) => (
              <button
                key={m}
                onClick={() => update({ autoLogoutMinutes: m })}
                className={`py-2.5 rounded-xl border text-xs transition-all ${
                  settings.autoLogoutMinutes === m
                    ? "border-purple-500/50 bg-purple-500/10 text-purple-300"
                    : "border-white/[0.06] text-white/40 hover:border-white/20"
                }`}
              >
                {m === 0 ? "Off" : `${m} min`}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Meeting Text Size ---- */}
        <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-white">Meeting Text Size</h3>
            <span className="text-xs text-white/30">{meetingFontSize}px</span>
          </div>
          <p className="text-xs text-white/30 mb-4">Relative to base font ({settings.fontSize}px)</p>

          <div className="grid grid-cols-3 gap-3 mb-4">
            {MEETING_SIZE_OPTIONS.map((opt) => {
              const sz =
                opt.id === "small" ? settings.fontSize - 2 :
                opt.id === "large" ? settings.fontSize + 2 : settings.fontSize
              return (
                <button
                  key={opt.id}
                  onClick={() => update({ meetingSize: opt.id as any })}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    settings.meetingSize === opt.id
                      ? "border-purple-500/50 bg-purple-500/10"
                      : "border-white/[0.06] hover:border-white/20"
                  }`}
                >
                  <div className="text-white mb-2 font-medium" style={{ fontSize: Math.max(sz, 8) }}>Aa</div>
                  <div className="text-xs font-medium text-white">{opt.label}</div>
                  <div className="text-[10px] text-white/30 mt-0.5">{Math.max(sz, 8)}px</div>
                </button>
              )
            })}
          </div>

          {/* 미리보기 */}
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.05] p-4">
            <p className="text-[10px] text-white/20 mb-2 uppercase tracking-wider">Meeting preview</p>
            <p className="meeting-preview-text text-white/70 leading-relaxed" style={{ fontSize: meetingFontSize }}>
              Based on current evidence, I propose that we focus on three key areas: user experience, performance, and scalability. 현재 증거에 따르면 세 가지 핵심 영역에 집중할 것을 제안합니다.
            </p>
          </div>
        </div>

      </div>
    </div>
  )
}